import { useEffect, useState } from 'react';
import { api, type SamlConnection } from '../api';
import {
  Banner, Card, CheckBox, Copyable, ListField, SelectField, TextField,
} from '../components/ui';
import { ConnectionPicker, NoConnections, useConnections } from '../components/useConnections';

const NAME_ID_FORMATS = [
  'urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified',
  'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
  'urn:oasis:names:tc:SAML:2.0:nameid-format:persistent',
  'urn:oasis:names:tc:SAML:2.0:nameid-format:transient',
  'urn:oasis:names:tc:SAML:1.1:nameid-format:X509SubjectName',
].map((v) => ({ value: v, label: v.replace(/^urn:oasis:names:tc:SAML:[\d.]+:nameid-format:/, '') }));

export function SamlTab({ tenantId }: { tenantId: string }) {
  const base = `/api/tenants/${tenantId}/saml`;
  const conn = useConnections<SamlConnection>(base);
  const [draft, setDraft] = useState<SamlConnection | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => { setDraft(conn.selected ? { ...conn.selected } : null); setSaved(false); }, [conn.selected]);

  if (conn.items === null) return <p className="faint">Loading…</p>;
  if (conn.items.length === 0) {
    return <NoConnections what="SAML connection" onCreate={conn.create} busy={conn.busy} />;
  }

  const set = <K extends keyof SamlConnection>(key: K, value: SamlConnection[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));

  const dirty = !!draft && !!conn.selected && JSON.stringify(draft) !== JSON.stringify(conn.selected);

  return (
    <>
      <ConnectionPicker
        items={conn.items} selectedId={conn.selectedId} onSelect={conn.setSelectedId}
        onCreate={conn.create} busy={conn.busy} newLabel="Connection name"
      />

      {conn.error && <Banner kind="error">{conn.error}</Banner>}
      {saved && !dirty && <Banner kind="success">Saved.</Banner>}

      {draft && (
        <>
          <SpDetails connection={draft} />
          <ImportMetadata base={base} id={draft.id} onImported={() => conn.load(draft.id)} />

          <Card
            title="Identity provider"
            hint="What this SP expects from the IdP. Import metadata above to fill these in automatically."
          >
            <div className="grid two">
              <TextField
                label="IdP entity ID" value={draft.idpEntityId} onChange={(v) => set('idpEntityId', v)} mono
                placeholder="https://idp.example.com/idp" hint="Compared against the Issuer in every response."
              />
              <TextField
                label="IdP SSO URL (HTTP-Redirect)" value={draft.idpSsoUrl} onChange={(v) => set('idpSsoUrl', v)} mono
                placeholder="https://idp.example.com/idp/SSO.saml2"
              />
              <TextField
                label="IdP Single Logout URL" value={draft.idpSloUrl} onChange={(v) => set('idpSloUrl', v)} mono
                placeholder="optional"
              />
            </div>
            <ListField
              label="IdP signing certificates"
              value={draft.idpCertificates}
              onChange={(v) => set('idpCertificates', v)}
              placeholder="MIIDdzCCAl+gAwIBAgIE..."
              hint="One per line, base64 or full PEM. Multiple entries let you roll a certificate without downtime."
            />
          </Card>

          <Card title="Protocol behaviour">
            <div className="grid two">
              <SelectField
                label="NameID format" value={draft.nameIdFormat}
                onChange={(v) => set('nameIdFormat', v)} options={NAME_ID_FORMATS}
              />
              <SelectField
                label="Signature algorithm" value={draft.signatureAlgorithm}
                onChange={(v) => set('signatureAlgorithm', v)}
                options={[
                  { value: 'sha256', label: 'RSA-SHA256' },
                  { value: 'sha512', label: 'RSA-SHA512' },
                  { value: 'sha1', label: 'RSA-SHA1 (legacy, insecure)' },
                ]}
              />
              <TextField
                label="Audience override" value={draft.audience} onChange={(v) => set('audience', v)} mono
                placeholder="defaults to the SP entity ID"
                hint="Set this when the IdP issues an AudienceRestriction that differs from the entity ID."
              />
              <TextField
                label="Clock skew tolerance (ms)" value={String(draft.clockSkewMs)}
                onChange={(v) => set('clockSkewMs', Number(v) || 0)} type="number"
                hint="Widen this if NotBefore checks fail because the IdP clock drifts."
              />
            </div>

            <div className="divider" />

            <div className="grid two">
              <div>
                <CheckBox
                  label="Require signed assertions" checked={draft.wantAssertionsSigned}
                  onChange={(v) => set('wantAssertionsSigned', v)}
                  hint="Recommended. Fails the test when the assertion is unsigned."
                />
                <CheckBox
                  label="Require signed response" checked={draft.wantResponseSigned}
                  onChange={(v) => set('wantResponseSigned', v)}
                  hint="Some IdPs sign the Response envelope instead of, or as well as, the assertion."
                />
                <CheckBox
                  label="Sign AuthnRequests" checked={draft.signAuthnRequest}
                  onChange={(v) => set('signAuthnRequest', v)}
                  hint="Uses the tenant SP key. Upload its certificate to the IdP first."
                />
              </div>
              <div>
                <CheckBox
                  label="ForceAuthn" checked={draft.forceAuthn} onChange={(v) => set('forceAuthn', v)}
                  hint="Ask the IdP to reauthenticate even with an existing session."
                />
                <CheckBox
                  label="IsPassive" checked={draft.isPassive} onChange={(v) => set('isPassive', v)}
                  hint="Ask the IdP not to show any UI."
                />
                <CheckBox
                  label="Enabled" checked={draft.enabled} onChange={(v) => set('enabled', v)}
                  hint="Disabled connections refuse new logins but keep their configuration."
                />
              </div>
            </div>

            <ListField
              label="Requested AuthnContextClassRef"
              value={draft.authnContext} onChange={(v) => set('authnContext', v)}
              placeholder="urn:oasis:names:tc:SAML:2.0:ac:classes:PasswordProtectedTransport"
              hint="One per line. Leave empty to omit RequestedAuthnContext entirely."
            />
          </Card>

          <div className="row" style={{ marginBottom: 30 }}>
            <button
              className="primary" disabled={!dirty || conn.busy}
              onClick={async () => { if (await conn.save(draft.id, draft)) setSaved(true); }}
            >{conn.busy ? 'Saving…' : 'Save changes'}</button>
            <button disabled={!dirty} onClick={() => setDraft({ ...conn.selected! })}>Discard</button>
            <div className="spacer" />
            <button
              className="danger"
              onClick={() => { if (confirm(`Delete SAML connection "${draft.name}"?`)) void conn.remove(draft.id); }}
            >Delete connection</button>
          </div>
        </>
      )}
    </>
  );
}

function SpDetails({ connection }: { connection: SamlConnection }) {
  const e = connection.endpoints;
  return (
    <Card
      title="Give these to your IdP"
      hint="Register this service provider at the identity provider using these values."
      actions={
        <a className="badge proto" href={e.metadataUrl} target="_blank" rel="noreferrer">SP metadata XML ↗</a>
      }
    >
      <Copyable label="SP entity ID" value={e.entityId} />
      <Copyable label="Assertion Consumer Service (HTTP-POST)" value={e.acsUrl} />
      <Copyable label="Single Logout Service" value={e.sloUrl} />
      <Copyable label="Signing certificate fingerprint (SHA-256)" value={e.signingCertificateFingerprint} />
      <div className="row" style={{ marginTop: 14 }}>
        <a className="badge proto" href={e.loginUrl} target="_blank" rel="noreferrer">
          Run SP-initiated login ↗
        </a>
        <span className="faint small">
          Opens the real flow: AuthnRequest → your IdP → back to the ACS with a full report.
        </span>
      </div>
    </Card>
  );
}

function ImportMetadata({ base, id, onImported }: { base: string; id: string; onImported: () => void }) {
  const [mode, setMode] = useState<'url' | 'xml'>('url');
  const [url, setUrl] = useState('');
  const [xml, setXml] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  const run = async () => {
    setBusy(true);
    setError('');
    setOk('');
    try {
      const body = mode === 'url' ? { url } : { xml };
      const res = await api.post<{ imported: { entityId: string; certificates: string[] } }>(
        `${base}/${id}/import-metadata`, body,
      );
      setOk(`Imported ${res.imported.entityId || 'metadata'} with ${res.imported.certificates.length} certificate(s).`);
      onImported();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="Import IdP metadata"
      hint="Fills in the entity ID, SSO/SLO URLs and signing certificates below."
      actions={
        <div className="row">
          <button className={mode === 'url' ? 'primary tiny' : 'tiny'} onClick={() => setMode('url')}>From URL</button>
          <button className={mode === 'xml' ? 'primary tiny' : 'tiny'} onClick={() => setMode('xml')}>Paste XML</button>
        </div>
      }
    >
      {error && <Banner kind="error">{error}</Banner>}
      {ok && <Banner kind="success">{ok}</Banner>}
      {mode === 'url' ? (
        <TextField
          label="Metadata URL" value={url} onChange={setUrl} mono
          placeholder="https://idp.example.com/pf/federation_metadata.ping?PartnerSpId=..."
          hint="Fetched by the server, so the URL only needs to be reachable from this host."
        />
      ) : (
        <label className="field">
          <span>Metadata XML</span>
          <textarea value={xml} onChange={(e) => setXml(e.target.value)} style={{ minHeight: 150 }}
            placeholder="&lt;EntityDescriptor ...&gt;" />
        </label>
      )}
      <button className="primary" disabled={busy || (mode === 'url' ? !url : !xml)} onClick={run}>
        {busy ? 'Importing…' : 'Import'}
      </button>
    </Card>
  );
}
